import React from 'react';
import UploadBanner from '../UploadBanner';
import VideoStage from '../VideoStage';
import AnalysisPanels from './AnalysisPanels';
import RenderStatusPanels from './RenderStatusPanels';
import EditedResultPanel from './EditedResultPanel';
import CapturePlanCard from './CapturePlanCard';
import CaptureCoverageStrip from './CaptureCoverageStrip';
import { deriveOverviewStage } from '../../utils/overviewStage';
import { persistFootageRotation } from '../../videoUpload/footageRotation';
import { isConcatInFlight } from '../../utils/footageConcat';
import { useProjectCoverage } from '../../hooks/useProjectPhotos';
import useFeatureFlag from '../../hooks/useFeatureFlag';
import { MOTION_PACE_CHART } from '../../lib/operationCopy';

const OverviewTab = (props) => {
    const {
        currentProject,
        isAnalysisPending,
        localVideoMeta,
        uploading,
        uploadProgress,
        analysisEta,
        generationEta,
        stoppingTask,
        onStopClick,
        projectId,
        videoUrl,
        editedVideoUrl,
        videoPlayerRef,
        progressRef,
        onSourcePlay,
        onEditedPlay,
        editedDurationSeconds,
        editedDurationPending = false,
        calculateEditedDuration,
        onTabChange,
    } = props;

    const { enabled: photoIngestEnabled } = useFeatureFlag('photoIngestV1', { defaultValue: true });
    const { enabled: capturePlanEnabled } = useFeatureFlag('capturePlanV1', { defaultValue: true });
    const { coverage, loading: coverageLoading } = useProjectCoverage(photoIngestEnabled ? projectId : null);
    const totalPhotos = coverage?.totalPhotos || 0;
    const hasPhotos = totalPhotos > 0;

    // The same one-field write the Footage tab makes. Nothing local is held:
    // the project snapshot is the single source of truth and turns every
    // preview a beat later on its own.
    const handleRotateFootage = React.useCallback(
        (next) => persistFootageRotation(projectId, next),
        [projectId],
    );

    const stage = deriveOverviewStage(currentProject, { uploading, isAnalysisPending, hasPhotos });

    // Plan-awareness cards sit at the top of the Overview across stages. The
    // coverage strip self-hides when there are no photos, so a video-only
    // project shows just the plan card.
    const planCards = capturePlanEnabled ? (
        <>
            <CapturePlanCard projectId={projectId} capturePlan={currentProject.capturePlan} />
            <CaptureCoverageStrip projectId={projectId} capturePlan={currentProject.capturePlan} />
        </>
    ) : null;

    const wrap = (content) => (
        <div>
            {planCards}
            {content}
        </div>
    );

    // The 'empty' vs 'empty_photos' split hinges on `hasPhotos`, which is
    // false until the coverage doc resolves. Committing to a panel while it
    // loads would flash the "add footage" CTA on a photo-first project before
    // swapping to the photo-aware panel. Hold on the plan cards until coverage
    // is known so the wrong empty-state never paints. Every other stage is
    // video/upload-driven and safe to render immediately.
    if (stage === 'empty' && coverageLoading) {
        return wrap(null);
    }

    // Overview never uploads directly — Footage is the single surface for both
    // video and photos. A bare project just points the user there.
    if (stage === 'empty') {
        return wrap(
            <button
                type="button"
                className="no-video-upload-container pv-add-footage-cta"
                onClick={() => onTabChange('footage')}
                data-testid="overview-empty-footage"
            >
                <svg className="no-video-upload-icon" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                <p>Add footage to get started</p>
                <p className="no-video-upload-hint">Upload a timelapse video or photos in the Footage tab</p>
            </button>
        );
    }

    if (stage === 'empty_photos') {
        return wrap(
            <div className="pv-empty-photos" data-testid="overview-empty-photos">
                <h3>{totalPhotos.toLocaleString()} photo{totalPhotos === 1 ? '' : 's'} uploaded</h3>
                <p>
                    Your photos are ready. Head to Footage to review them by capture day and
                    compile them into a timelapse.
                </p>
                <button
                    type="button"
                    className="btn btn-teal"
                    onClick={() => onTabChange('footage')}
                    data-testid="overview-empty-photos-cta"
                >
                    Go to Footage
                </button>
            </div>
        );
    }

    if (stage === 'uploading') {
        return wrap(
            <UploadBanner state="uploading" uploadProgress={uploadProgress} />
        );
    }

    if (stage === 'analyzing' || stage === 'analysis_failed' || stage === 'analysis_cancelled') {
        return wrap(
            <>
                <UploadBanner project={currentProject} state="uploaded" localVideoMeta={localVideoMeta} />
                <AnalysisPanels
                    currentProject={currentProject}
                    isAnalysisPending={isAnalysisPending}
                    analysisEta={analysisEta}
                    stoppingTask={stoppingTask}
                    onStopClick={onStopClick}
                    projectId={projectId}
                />
            </>
        );
    }

    if (stage === 'rendering' || stage === 'render_failed' || stage === 'render_cancelled') {
        return wrap(
            <>
                <UploadBanner project={currentProject} state="uploaded" localVideoMeta={localVideoMeta} />
                <RenderStatusPanels
                    currentProject={currentProject}
                    generationEta={generationEta}
                    stoppingTask={stoppingTask}
                    onStopClick={onStopClick}
                    progressRef={progressRef}
                />
            </>
        );
    }

    if (stage === 'edit_complete') {
        return wrap(
            <>
                <UploadBanner project={currentProject} state="uploaded" localVideoMeta={localVideoMeta} />
                <EditedResultPanel
                    currentProject={currentProject}
                    editedVideoUrl={editedVideoUrl}
                    onEditedPlay={onEditedPlay}
                    projectId={projectId}
                />
                <div className="pv-center-button-container">
                    <button className="pv-tab-secondary-cta" onClick={() => onTabChange('edits')}>
                        Fine-tune in Editor
                    </button>
                </div>
            </>
        );
    }

    // ready_to_edit
    const motionSegments = currentProject.video?.motionSegments;
    const fps = currentProject.video?.fps;
    const width = currentProject.video?.resolution?.width;
    const height = currentProject.video?.resolution?.height;
    return wrap(
        <>
            <UploadBanner project={currentProject} state="uploaded" localVideoMeta={localVideoMeta} />
            <div className="pv-main-content">
                <div className="pv-left-column">
                    {/* Source footage, so it carries the project's rotation —
                        the same wrap as the Footage and Edit tabs. The
                        EditedResultPanel further down plays the finished
                        render, which already has the rotation baked in and is
                        deliberately left alone. */}
                    {videoUrl && (
                        <VideoStage
                            src={videoUrl}
                            ref={videoPlayerRef}
                            rotation={currentProject.footageRotation}
                            width={currentProject.video?.resolution?.width}
                            height={currentProject.video?.resolution?.height}
                            onPlay={onSourcePlay}
                            // Rotation is a property of the footage, so it is
                            // offered wherever the footage is shown rather than
                            // only on the tab that happens to own the setting.
                            // The write is the same single field either way,
                            // and the snapshot turns every surface at once —
                            // which is also why the combine lock has to follow
                            // it here rather than living only on Footage.
                            onRotate={handleRotateFootage}
                            rotateDisabled={isConcatInFlight(currentProject.videoConcat)}
                        />
                    )}
                </div>
            </div>
            <div className="pv-full-width">
                <div className="edited-video-stats">
                    <div className="stat-item">
                        <span className="stat-label">Motion Segments</span>
                        <span className="stat-value">{motionSegments?.length || 0}</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label">Edited Duration</span>
                        {/* `--` while the stored speed curve is behind the
                            controls — see the same cell in EditorPanel.js.

                            CARRIES ITS OWN EXPLANATION, unlike that copy. The
                            editor's cell can defer to the pace chart sitting
                            directly above it; Overview has no Motion section, so
                            a bare `--` here would be a length that vanished with
                            nothing on screen saying why. The `title` alone does
                            not cover it — tooltips never appear on touch — so the
                            reason is also the accessible name, and `role="status"`
                            announces the swap when a save flips this mid-view. */}
                        <span
                            className="stat-value"
                            data-testid="stat-edited-duration"
                            title={editedDurationPending ? MOTION_PACE_CHART.durationPending : undefined}
                            {...(editedDurationPending ? {
                                role: 'status',
                                'aria-live': 'polite',
                                'aria-label': MOTION_PACE_CHART.durationPending,
                            } : {})}
                        >
                            {editedDurationPending ? '--' : calculateEditedDuration(editedDurationSeconds)}
                        </span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label">Final FPS</span>
                        <span className="stat-value">{fps ? Math.round(fps) : '--'}</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label">Final Resolution</span>
                        <span className="stat-value">{(width && height) ? `${width}x${height}` : '--'}</span>
                    </div>
                </div>
            </div>
            <div className="pv-center-button-container">
                <button className="btn btn-teal" onClick={() => onTabChange('edits')}>
                    Open Editor
                </button>
            </div>
        </>
    );
};

export default OverviewTab;
